import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RefreshCw, Save, ArrowLeft, Copy, Check, Loader2, Pencil, Eye, Sparkles, ImageIcon, Download } from "lucide-react";
import { toast } from "sonner";
import VideoPreview from "@/components/VideoPreview";
import { renderSquareCreative, renderStoryCover, renderCarouselSlide, downloadCanvas, slugify } from "@/lib/creativeCanvas";
import { CREATIVE_STYLES, pickBadge, boatSpecChips } from "@/lib/creativeStyles";

// True WYSIWYG preview: renders the EXACT canvas used for the PNG export
// (renderSquareCreative / renderStoryCover) and displays it scaled to fit.
// Scaling only changes the display size in CSS - the 1080px bitmap, composition,
// typography, wrapping, overlays, borders, badges and logo are identical to the
// downloaded image. No separate CSS approximation is kept.
function CanvasCreative({ render, deps, aspect = "1 / 1", testid, onReady, maxWidth, className = "" }) {
  const holder = useRef(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    setErr(false);
    (async () => {
      try {
        const canvas = await render();
        if (!alive || !holder.current) return;
        canvas.style.width = "100%";
        canvas.style.height = "100%";
        canvas.style.display = "block";
        holder.current.innerHTML = "";
        holder.current.appendChild(canvas);
        if (onReady) onReady(canvas);
      } catch (e) { console.error(e); if (alive) setErr(true); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return (
    <div className={`relative w-full mx-auto bg-navy overflow-hidden ${className}`} style={{ aspectRatio: aspect, maxWidth }}>
      <div ref={holder} className="w-full h-full" data-testid={testid} />
      {err && <div className="absolute inset-0 flex items-center justify-center text-white/70 text-xs p-4 text-center">Preview unavailable - use a boat with an uploaded photo.</div>}
    </div>
  );
}

function CopyBtn({ text, testid }) {
  const [done, setDone] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text || "");
    setDone(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <Button variant="outline" size="sm" onClick={copy} data-testid={testid} className="shrink-0">
      {done ? <Check className="w-3.5 h-3.5 pointer-events-none" /> : <Copy className="w-3.5 h-3.5 pointer-events-none" />}
    </Button>
  );
}

const Panel = ({ title, children, right }) => (
  <div className="bg-white rounded-xl border border-border p-5">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-semibold text-navy">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const clone = (o) => JSON.parse(JSON.stringify(o));

export default function CampaignResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(location.state);
  const [originalResults, setOriginalResults] = useState(() =>
    location.state?.results ? clone(location.state.results) : null);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const canvasCache = useRef({});

  const _cfg0 = location.state?.config || {};
  const _rj0 = location.state?.recjet || _cfg0.recjet || null;
  const _geoRec0 = _rj0?.geo_targeting || null;
  const [budget, setBudget] = useState(
    _cfg0.budget != null ? _cfg0.budget : (_rj0?.recommended_budget ?? ""));
  const [markets, setMarkets] = useState(
    ((_cfg0.targeting?.markets && _cfg0.targeting.markets.length ? _cfg0.targeting.markets : (_geoRec0?.markets || [])) || []).join(", "));
  const [radius, setRadius] = useState(
    _cfg0.targeting?.radius_miles ?? _geoRec0?.recommended_radius_miles ?? "");

  if (!state?.results) return <Navigate to="/create-campaign" replace />;
  const { results, boat, config } = state;
  const recjet = state.recjet || config?.recjet || null;
  const geoRec = recjet?.geo_targeting || null;
  const budgetCtx = recjet?.budget_context || null;
  const isOrganic = recjet ? (recjet.organic_paid_strategy === "organic" || !recjet.recommended_budget) : false;
  const overLimit = !isOrganic && budgetCtx?.normal_campaign_max != null && budget !== "" && Number(budget) > budgetCtx.normal_campaign_max;
  const heroImg = results.main_graphic?.image || boat?.photos?.[0];
  const photos = (config?.selected_photos?.length ? config.selected_photos : (boat?.photos || []));

  const update = (updater) => {
    setState((s) => {
      const next = clone(s.results);
      updater(next);
      return { ...s, results: next };
    });
  };
  const setField = (path) => (e) => {
    const value = e.target.value;
    update((r) => {
      const keys = path.split(".");
      let ref = r;
      for (let i = 0; i < keys.length - 1; i++) ref = ref[keys[i]];
      ref[keys[keys.length - 1]] = value;
    });
  };
  const setHeadline = (i) => (e) => { const v = e.target.value; update((r) => { r.headlines[i] = v; }); };
  const setPoint = (i) => (e) => { const v = e.target.value; update((r) => { r.selling_points[i] = v; }); };
  const setScene = (i) => (e) => { const v = e.target.value; update((r) => { r.storyboard[i].on_screen_text = v; }); };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post("/campaigns/generate", config);
      setState((s) => ({ ...s, results: data.results }));
      setOriginalResults(clone(data.results));
      toast.success("Fresh AI campaign generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Regenerate failed");
    } finally {
      setRegenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const _marketList = (markets || "").split(",").map((s) => s.trim()).filter(Boolean);
      const _paidBudget = isOrganic ? null : (budget === "" || budget == null ? null : Number(budget));
      const _targeting = { markets: _marketList,
                           radius_miles: radius === "" || radius == null ? null : Number(radius),
                           buyer_geo_note: geoRec?.buyer_geo_note || null };
      await api.post("/campaigns", { ...config, results, original_results: originalResults,
        budget: _paidBudget, targeting: _targeting, territory: _marketList[0] || null,
        recommended_visual_direction: results.recommended_creative != null ? (CREATIVE_STYLES[results.recommended_creative] || CREATIVE_STYLES[0]).id : null,
        selected_visual_direction: (CREATIVE_STYLES[results.selected_creative ?? 0] || CREATIVE_STYLES[0]).id });
      toast.success("Campaign saved to your library");
      navigate("/campaigns");
    } catch (e) {
      console.error("Save campaign failed:", e.response?.data?.detail || e.message);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "text-navy";

  const baseName = slugify(`${boat?.year || ""} ${boat?.manufacturer || ""} ${boat?.model || ""}`);
  const specs = boatSpecChips(boat);
  const logoLine = results.main_graphic?.logo_line;
  const logoImg = results.main_graphic?.logo || "";
  const dlCreative = async (opt, i) => {
    try {
      const cached = canvasCache.current[`c${i}`];
      const canvas = cached || await renderSquareCreative({
        photo: opt.photos?.[0] || heroImg, style: CREATIVE_STYLES[i] || CREATIVE_STYLES[0], subheadline: opt.subheadline,
        headline: opt.headline, cta: opt.cta, specs,
        logoLine, logoImg, badge: pickBadge(boat, i),
      });
      await downloadCanvas(canvas, `${baseName}-option-${i + 1}.png`);
      toast.success("PNG downloaded", { id: "dl" });
    } catch (e) { console.error(e); toast.error("Could not render this image. Boats with photos uploaded to your dealership work best."); }
  };
  const dlStory = async () => {
    try {
      const selIdx = results.selected_creative || 0;
      const sel = results.creative_options?.[selIdx];
      const canvas = canvasCache.current.story || await renderStoryCover({
        photo: heroImg, logoLine, logoImg, style: CREATIVE_STYLES[selIdx] || CREATIVE_STYLES[1], badge: pickBadge(boat, selIdx),
        headline: sel?.headline || results.main_graphic?.headline, cta: sel?.cta || results.main_graphic?.cta, specs,
      });
      await downloadCanvas(canvas, `${baseName}-story-cover.png`);
      toast.success("Story cover downloaded", { id: "dl" });
    } catch (e) { console.error(e); toast.error("Could not render the story cover."); }
  };
  const dlSlide = async (photo, i) => {
    const canvas = await renderCarouselSlide({ photo, caption: results.selling_points?.[i] || `Highlight ${i + 1}`, index: i + 1 });
    await downloadCanvas(canvas, `${baseName}-carousel-${i + 1}.png`);
  };
  const dlOneSlide = async (photo, i) => {
    try { await dlSlide(photo, i); toast.success("Slide downloaded", { id: "dl" }); }
    catch (e) { console.error(e); toast.error("Could not render this slide."); }
  };
  const dlAllSlides = async () => {
    const slides = photos.slice(0, 6);
    let ok = 0;
    for (let i = 0; i < slides.length; i++) {
      try { await dlSlide(slides[i], i); ok += 1; } catch (e) { console.error(e); }
    }
    if (ok) toast.success(`Downloaded ${ok} slide${ok > 1 ? "s" : ""}`, { id: "dl" });
    else toast.error("Could not render the carousel.");
  };

  return (
    <div className="space-y-6 fade-up" data-testid="campaign-results-page">
      {recjet && (
        <div className="rounded-2xl border border-gold/40 bg-navy text-white p-4 flex items-start gap-3" data-testid="recjet-built-summary">
          <div className="w-8 h-8 rounded-lg gold-gradient flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4 text-navy" /></div>
          <div className="text-sm">
            <div className="font-bold text-gold uppercase tracking-widest text-[11px]">Built by RecJet Mind</div>
            <div className="mt-0.5">Boat: <span className="font-semibold">{boat?.year} {boat?.manufacturer} {boat?.model}</span></div>
            {recjet.strategy_label && <div>Strategy: <span className="font-semibold">{recjet.strategy_label}</span></div>}
            {recjet.best_fit_buyer && <div data-testid="recjet-built-buyer">Best-fit buyer: <span className="font-semibold">{recjet.best_fit_buyer}</span></div>}
            {recjet.suggested_message && <div className="text-slate-300">Message direction: {recjet.suggested_message}</div>}
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <p className="text-gold text-sm uppercase tracking-widest font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> {results.concept_title || "Campaign Generated"}
          </p>
          <h1 className="font-serif text-3xl font-bold text-navy">{boat?.year} {boat?.manufacturer} {boat?.model}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {recjet && (
            <Button variant="outline" onClick={() => navigate(
              `/create-campaign?category=${encodeURIComponent(recjet.category || "")}${recjet.recommended_boat?.id ? `&boatId=${encodeURIComponent(recjet.recommended_boat.id)}` : ""}&fromRecjet=1`,
              { state: { recjet } })} data-testid="adjust-campaign-btn">
              <Pencil className="w-4 h-4 mr-1.5 pointer-events-none" /> Adjust Campaign
            </Button>
          )}
          <Button variant={editing ? "default" : "outline"} onClick={() => setEditing((v) => !v)}
            className={editing ? "bg-gold text-navy hover:bg-gold/90" : ""} data-testid="edit-toggle-btn">
            {editing ? <Eye className="w-4 h-4 mr-1.5 pointer-events-none" /> : <Pencil className="w-4 h-4 mr-1.5 pointer-events-none" />}
            {editing ? "Preview" : "Edit Content"}
          </Button>
          <Button variant="outline" onClick={regenerate} disabled={regenerating} data-testid="regenerate-btn">
            {regenerating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5 pointer-events-none" /> : <RefreshCw className="w-4 h-4 mr-1.5 pointer-events-none" />} Regenerate
          </Button>
          <Button onClick={save} disabled={saving} className="bg-navy hover:bg-navy-light text-white" data-testid="save-campaign-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5 pointer-events-none" /> : <Save className="w-4 h-4 mr-1.5 pointer-events-none" />} Save Campaign
          </Button>
        </div>
      </div>

      {editing && (
        <div className="inline-flex items-center gap-2 text-xs bg-navy/5 text-navy px-3 py-1.5 rounded-full border border-navy/20" data-testid="editing-badge">
          <Pencil className="w-3 h-3" /> Editing - your edits are saved alongside the original AI version when you click Save Campaign
        </div>
      )}

      {/* Budget & Geographic Targeting - editable recommendation; no spend is authorized */}
      <Panel title="Budget & Geographic Targeting">
        <div data-testid="campaign-budget-targeting" className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs">Recommended Advertising Budget</Label>
            {isOrganic ? (
              <div className="text-sm text-muted-foreground" data-testid="campaign-budget-organic">
                This is an organic recommendation. No paid advertising budget is required.
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-navy font-semibold">$</span>
                  <Input type="number" min="0" value={budget} onChange={(e) => setBudget(e.target.value)}
                    className={inputCls} data-testid="campaign-budget-input" />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Total dealer spend for this campaign (not a daily budget). You can adjust this before approval.
                </p>
                {recjet?.recommended_budget != null && (
                  <p className="text-[11px] text-muted-foreground" data-testid="campaign-budget-recommended">
                    RecJet Mind recommended: <span className="font-semibold text-navy">${Math.round(recjet.recommended_budget)}</span>
                  </p>
                )}
                {budgetCtx?.normal_campaign_max != null && (
                  <p className="text-[11px] text-muted-foreground" data-testid="campaign-budget-limit">
                    Your authorized per-campaign limit: <span className="font-semibold text-navy">${Math.round(budgetCtx.normal_campaign_max)}</span> (from your Media Plan)
                  </p>
                )}
                {overLimit && (
                  <p className="text-[11px] font-semibold text-amber-600" data-testid="campaign-budget-over-limit">
                    This exceeds your authorized per-campaign limit. Adjust the amount or update your Media Plan.
                  </p>
                )}
                {recjet?.spend_explanation && (
                  <p className="text-[11px] text-muted-foreground">{recjet.spend_explanation}</p>
                )}
                <p className="text-[11px] text-muted-foreground">No spend is authorized or started. This is a recommendation only.</p>
              </>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Geographic Targeting</Label>
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Markets (cities / regions, comma separated)</Label>
              <Input value={markets} onChange={(e) => setMarkets(e.target.value)} placeholder="Add your target markets"
                className={inputCls} data-testid="campaign-geo-markets-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Radius (miles)</Label>
              <Input type="number" min="0" value={radius} onChange={(e) => setRadius(e.target.value)}
                className={inputCls} data-testid="campaign-geo-radius-input" />
            </div>
            {geoRec && geoRec.configured === false && (!geoRec.markets || geoRec.markets.length === 0) && (
              <p className="text-[11px] text-muted-foreground" data-testid="campaign-geo-empty">
                No geographic markets are configured yet. Set your market area in your Dealership Profile so RecJet Mind can recommend targeting.
              </p>
            )}
            {geoRec?.buyer_geo_note && (
              <p className="text-[11px] text-muted-foreground" data-testid="campaign-geo-buyer-note">
                Buyer geography: {geoRec.buyer_geo_note}
              </p>
            )}
            <p className="text-[11px] text-muted-foreground">Review and adjust before proceeding. No campaign is created on any ad platform.</p>
          </div>
        </div>
      </Panel>

      {/* Creative Options - three distinct concepts, real photos only */}
      <section data-testid="creative-options" className="space-y-4">
        <div>
          <h2 className="font-serif text-2xl font-bold text-navy">Creative Options</h2>
          <p className="text-muted-foreground text-sm">Three distinct ad concepts for this boat - pick the direction you like. All use your real photos only.</p>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          {(results.creative_options || []).map((opt, i) => {
            const chosen = results.selected_creative === i;
            const st = CREATIVE_STYLES[i] || CREATIVE_STYLES[0];
            const badge = pickBadge(boat, i);
            return (
              <div key={i} className={`bg-white rounded-2xl border-2 overflow-hidden card-hover ${chosen ? "border-gold ring-2 ring-gold/30" : "border-border"}`} data-testid={`creative-option-${i}`}>
                {/* UI labels live OUTSIDE the creative - app chrome, not part of the exported PNG */}
                <div className="flex items-center justify-between px-4 pt-4 pb-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-navy bg-secondary px-2.5 py-1 rounded-full" data-testid={`option-label-${i}`}>Option {i + 1}</span>
                  <div className="flex items-center gap-1.5">
                    {results.recommended_creative === i && <span className="text-[10px] font-bold uppercase tracking-wider text-navy bg-gold px-2.5 py-1 rounded-full" data-testid={`recommended-label-${i}`}>Recommended</span>}
                    {chosen && <span className="text-[10px] font-bold uppercase tracking-wider text-white bg-emerald-600 px-2.5 py-1 rounded-full flex items-center gap-1" data-testid={`in-use-label-${i}`}><Check className="w-3 h-3" /> In Use</span>}
                  </div>
                </div>
                <div className="px-4">
                  <CanvasCreative
                    testid={`creative-canvas-${i}`}
                    aspect="1 / 1"
                    className="rounded-lg"
                    onReady={(cv) => { canvasCache.current[`c${i}`] = cv; }}
                    deps={[i, opt.headline, opt.subheadline, opt.cta, logoImg, specs.join("|"), badge, opt.photos?.[0], heroImg]}
                    render={() => renderSquareCreative({
                      photo: opt.photos?.[0] || heroImg, style: st, subheadline: opt.subheadline,
                      headline: opt.headline, cta: opt.cta, specs,
                      logoLine, logoImg, badge,
                    })}
                  />
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${i === 0 ? "bg-slate-100 text-slate-600" : i === 1 ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>{st.name}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">{st.tagline} - {opt.approach}</p>
                  {editing ? (
                    <div className="space-y-2" data-testid={`edit-creative-${i}`}>
                      <div className="space-y-1"><Label className="text-xs">Headline</Label><Input value={opt.headline || ""} onChange={setField(`creative_options.${i}.headline`)} className={inputCls} data-testid={`edit-creative-headline-${i}`} /></div>
                      <div className="space-y-1"><Label className="text-xs">Subheadline</Label><Input value={opt.subheadline || ""} onChange={setField(`creative_options.${i}.subheadline`)} className={inputCls} data-testid={`edit-creative-subheadline-${i}`} /></div>
                      <div className="space-y-1"><Label className="text-xs">CTA</Label><Input value={opt.cta || ""} onChange={setField(`creative_options.${i}.cta`)} className={inputCls} data-testid={`edit-creative-cta-${i}`} /></div>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Uses {opt.photo_labels?.length ? opt.photo_labels.join(", ") : "Photo 1"} - real photography, no AI alteration.</p>
                  )}
                  <Button
                    onClick={() => { update((r) => { r.selected_creative = i; }); toast.success(`Creative Option ${i + 1} selected`); }}
                    className={`w-full ${chosen ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-navy hover:bg-navy-light text-white"}`}
                    data-testid={`use-creative-${i}`}
                  >
                    {chosen ? <><Check className="w-4 h-4 mr-1.5" /> Using This Creative</> : "Use This Creative"}
                  </Button>
                  <Button variant="outline" onClick={() => dlCreative(opt, i)} className="w-full border-2" data-testid={`download-creative-${i}`}>
                    <Download className="w-4 h-4 mr-1.5" /> Download PNG
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Vertical social video */}
        <Panel title="Vertical Social Video">
          <VideoPreview photos={photos} storyboard={results.storyboard} caption={results.video?.caption}
            style={CREATIVE_STYLES[results.selected_creative || 0]} badge={pickBadge(boat, results.selected_creative || 0)} />
        </Panel>

        {/* Story / Reel cover - 9:16, true WYSIWYG canvas render, real photo only */}
        <Panel title="Story / Reel Cover">
          <div className="flex justify-center">
            <CanvasCreative
              testid="story-cover"
              aspect="9 / 16"
              maxWidth="12rem"
              className="rounded-2xl"
              onReady={(cv) => { canvasCache.current.story = cv; }}
              deps={[heroImg, results.selected_creative, results.creative_options?.[results.selected_creative || 0]?.headline, results.creative_options?.[results.selected_creative || 0]?.cta, logoImg, specs.join("|")]}
              render={() => {
                const selIdx = results.selected_creative || 0;
                const sel = results.creative_options?.[selIdx];
                return renderStoryCover({
                  photo: heroImg, logoLine, logoImg, style: CREATIVE_STYLES[selIdx] || CREATIVE_STYLES[1], badge: pickBadge(boat, selIdx),
                  headline: sel?.headline || results.main_graphic?.headline, cta: sel?.cta || results.main_graphic?.cta, specs,
                });
              }}
            />
          </div>
          <p className="text-[11px] text-muted-foreground mt-2 text-center">Exact preview of your downloaded 9:16 PNG. Real dealer photography only.</p>
          <div className="flex justify-center mt-2">
            <Button variant="outline" onClick={dlStory} className="border-2" data-testid="download-story">
              <Download className="w-4 h-4 mr-1.5" /> Download PNG
            </Button>
          </div>
        </Panel>
      </div>

      {/* Carousel ad placeholder - 4-6 real photos */}
      <Panel title="Carousel Ad" right={photos.length ? <Button variant="outline" size="sm" onClick={dlAllSlides} data-testid="download-carousel-all"><Download className="w-3.5 h-3.5 mr-1.5" /> Download All</Button> : null}>
        {photos.length ? (
          <>
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="carousel-ad">
              {photos.slice(0, 6).map((p, i) => (
                <div key={i} className="relative w-40 shrink-0 rounded-xl overflow-hidden bg-navy group" data-testid={`carousel-slide-${i}`}>
                  <div className="aspect-square"><img src={resolveImg(p)} alt="" className="w-full h-full object-cover" /></div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy/90 to-transparent p-2">
                    <div className="text-white text-xs font-semibold line-clamp-2">{results.selling_points?.[i] || `Highlight ${i + 1}`}</div>
                  </div>
                  <span className="absolute top-2 left-2 text-[10px] font-bold text-navy bg-gold px-1.5 py-0.5 rounded">{i + 1}</span>
                  <button onClick={() => dlOneSlide(p, i)} data-testid={`download-slide-${i}`} title="Download slide"
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 text-navy flex items-center justify-center hover:bg-white shadow">
                    <Download className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              {photos.length < 4
                ? `Add ${4 - photos.length} more photo(s) for a full 4-6 slide carousel. `
                : ""}
              Each slide pairs a real photo with a feature/selling point. Placeholder - finished rendering connects later.
            </p>
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground"><ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-30" />Add photos to this boat to build a carousel (4-6 recommended).</div>
        )}
      </Panel>

      <div className="grid grid-cols-1 gap-6">
        {/* Copy + content outputs */}
        <div>
          <Tabs defaultValue="fb" className="w-full">
            <TabsList className="bg-white border border-border w-full justify-start overflow-x-auto flex-wrap h-auto" data-testid="copy-tabs">
              <TabsTrigger value="fb" data-testid="tab-fb">FB / IG Ad</TabsTrigger>
              <TabsTrigger value="organic" data-testid="tab-organic">Organic Caption</TabsTrigger>
              <TabsTrigger value="headlines" data-testid="tab-headlines">Headlines</TabsTrigger>
              <TabsTrigger value="selling" data-testid="tab-selling">Selling Points</TabsTrigger>
              <TabsTrigger value="cta" data-testid="tab-cta">Call to Action</TabsTrigger>
              <TabsTrigger value="email" data-testid="tab-email">Email</TabsTrigger>
              <TabsTrigger value="sms" data-testid="tab-sms">SMS</TabsTrigger>
              <TabsTrigger value="storyboard" data-testid="tab-storyboard">Reel Storyboard</TabsTrigger>
            </TabsList>

            <TabsContent value="fb" className="mt-4">
              <Panel title="Facebook / Instagram Ad Copy" right={!editing && <CopyBtn text={results.fb_ig_copy} testid="copy-fb" />}>
                {editing
                  ? <Textarea value={results.fb_ig_copy || ""} onChange={setField("fb_ig_copy")} rows={5} className={inputCls} data-testid="edit-fb-copy" />
                  : <p className="text-navy/90 whitespace-pre-wrap leading-relaxed" data-testid="fb-copy">{results.fb_ig_copy}</p>}
              </Panel>
            </TabsContent>

            <TabsContent value="organic" className="mt-4">
              <Panel title="Organic Social Caption" right={!editing && <CopyBtn text={results.organic_caption} testid="copy-organic" />}>
                {editing
                  ? <Textarea value={results.organic_caption || ""} onChange={setField("organic_caption")} rows={5} className={inputCls} data-testid="edit-organic-copy" />
                  : <p className="text-navy/90 whitespace-pre-wrap leading-relaxed" data-testid="organic-copy">{results.organic_caption}</p>}
              </Panel>
            </TabsContent>

            <TabsContent value="headlines" className="mt-4">
              <Panel title="Three Headline Options">
                <div className="space-y-2" data-testid="headlines-list">
                  {results.headlines?.map((h, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-secondary">
                      {editing
                        ? <Input value={h} onChange={setHeadline(i)} className={`${inputCls} bg-white`} data-testid={`edit-headline-${i}`} />
                        : <span className="font-semibold text-navy">{i + 1}. {h}</span>}
                      {!editing && <CopyBtn text={h} testid={`copy-headline-${i}`} />}
                    </div>
                  ))}
                </div>
              </Panel>
            </TabsContent>

            <TabsContent value="selling" className="mt-4">
              <Panel title="Five Recommended Selling Points">
                <ul className="space-y-2" data-testid="selling-points-list">
                  {results.selling_points?.map((s, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 rounded-lg bg-secondary">
                      <span className="w-6 h-6 rounded-full gold-gradient text-navy text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                      {editing
                        ? <Input value={s} onChange={setPoint(i)} className={`${inputCls} bg-white`} data-testid={`edit-selling-${i}`} />
                        : <span className="text-navy/90" data-testid={`selling-point-${i}`}>{s}</span>}
                    </li>
                  ))}
                </ul>
              </Panel>
            </TabsContent>

            <TabsContent value="cta" className="mt-4">
              <Panel title="Call To Action">
                {editing ? (
                  <div className="grid sm:grid-cols-2 gap-4" data-testid="edit-cta-fields">
                    <div className="space-y-1.5"><Label>Primary CTA</Label><Input value={results.cta?.primary || ""} onChange={setField("cta.primary")} className={inputCls} data-testid="edit-cta-primary" /></div>
                    <div className="space-y-1.5"><Label>Secondary CTA (optional)</Label><Input value={results.cta?.secondary || ""} onChange={setField("cta.secondary")} className={inputCls} data-testid="edit-cta-secondary" /></div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-3" data-testid="cta-list">
                    {results.cta?.primary && <span className="gold-gradient text-navy font-bold px-5 py-2.5 rounded-full">{results.cta?.primary}</span>}
                    {results.cta?.secondary && <span className="border-2 border-navy text-navy font-bold px-5 py-2.5 rounded-full">{results.cta?.secondary}</span>}
                  </div>
                )}
              </Panel>
            </TabsContent>

            <TabsContent value="email" className="mt-4">
              <Panel title="Email Marketing Copy" right={!editing && <CopyBtn text={`${results.email?.subject}\n\n${results.email?.body?.replace(/<[^>]+>/g, "")}`} testid="copy-email" />}>
                {editing ? (
                  <div className="space-y-3" data-testid="edit-email-fields">
                    <div className="space-y-1.5"><Label>Subject</Label><Input value={results.email?.subject || ""} onChange={setField("email.subject")} className={inputCls} data-testid="edit-email-subject" /></div>
                    <div className="space-y-1.5"><Label>Body (HTML supported)</Label><Textarea value={results.email?.body || ""} onChange={setField("email.body")} rows={8} className={`${inputCls} font-mono text-xs`} data-testid="edit-email-body" /></div>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="email-copy">
                    <div className="text-sm"><span className="text-muted-foreground">Subject: </span><span className="font-semibold text-navy">{results.email?.subject}</span></div>
                    <div className="border rounded-lg p-4 bg-secondary/50 text-navy/90 text-sm" dangerouslySetInnerHTML={{ __html: results.email?.body }} />
                  </div>
                )}
              </Panel>
            </TabsContent>

            <TabsContent value="sms" className="mt-4">
              <Panel title="SMS Marketing Copy" right={!editing && <CopyBtn text={results.sms} testid="copy-sms" />}>
                <div className="max-w-sm">
                  {editing
                    ? <Textarea value={results.sms || ""} onChange={setField("sms")} rows={4} className={inputCls} data-testid="edit-sms-copy" />
                    : <div className="bg-navy text-white rounded-2xl rounded-bl-sm p-4 text-sm leading-relaxed" data-testid="sms-copy">{results.sms}</div>}
                  <div className="text-xs text-muted-foreground mt-1.5">{results.sms?.length || 0} characters</div>
                </div>
              </Panel>
            </TabsContent>

            <TabsContent value="storyboard" className="mt-4">
              <Panel title="15-Second Reel Storyboard">
                <p className="text-[11px] text-muted-foreground mb-4">Built from your real uploaded photos only - pan/zoom, transitions and text overlays. No synthetic or AI-altered boat imagery.</p>
                <div className="space-y-3" data-testid="storyboard-list">
                  {results.storyboard?.length ? results.storyboard.map((sc, i) => {
                    const thumb = photos.length ? photos[i % photos.length] : heroImg;
                    return (
                      <div key={i} className="flex gap-4 p-3 rounded-lg bg-secondary" data-testid={`storyboard-scene-${i}`}>
                        <div className="relative w-24 h-24 rounded-lg overflow-hidden bg-navy shrink-0">
                          {thumb ? <img src={resolveImg(thumb)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-6 h-6 text-white/40" /></div>}
                          <span className="absolute top-1 left-1 bg-gold text-navy text-[10px] font-bold px-1.5 py-0.5 rounded">{sc.timecode || `#${i + 1}`}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground">Scene {sc.scene || i + 1} · {sc.photo || `Photo ${(i % Math.max(photos.length, 1)) + 1}`}</div>
                          <div className="text-sm text-navy/80 mb-1">{sc.shot}</div>
                          {editing
                            ? <Input value={sc.on_screen_text || ""} onChange={setScene(i)} className={`${inputCls} bg-white`} data-testid={`edit-scene-${i}`} />
                            : <div className="font-semibold text-navy" data-testid={`scene-text-${i}`}>“{sc.on_screen_text}”</div>}
                        </div>
                      </div>
                    );
                  }) : <div className="text-muted-foreground text-sm">No storyboard returned.</div>}
                </div>
              </Panel>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Button variant="ghost" onClick={() => navigate("/inventory")} className="text-muted-foreground" data-testid="results-back-inventory">
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Inventory
      </Button>
    </div>
  );
}
